import {
    DialogContent,
    TextField,
    Button,
    useTheme,
} from "@mui/material";
import { useState } from "react";
import TemplateClient, { Template, TemplateErrors } from "../apiClients/TemplateClient";
import { useErrorDisplay } from "../components/ErrorDisplay"
import BusySpinner from "../components/BusySpinner";
import IconButtonEx, { IconButtonExProps } from "../components/IconButtonEx";
import { ProcessType } from "../models/dtos";
import PositionAutocompleteControl from "../components/PositionAutocompleteControl";
import { I18N } from "../I18N";
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { IsEmpty } from "../Helpers";
import { MobileFormStack, ResponsiveDialog, ResponsiveDialogHeader, StickyDialogActions } from "../components/responsive";

const newTemplate = {
    Name: "",
    TemplateRevisionId: "",
    Revision: 1,
    PositionId: '',
    ProcessType: ProcessType.Evaluate,
    IsActive: false,
    Id: null,
    HasTemplate: false,
    IsDeleted: false,
    LastUpdateDate: ""
}

export interface TemplateCopyProps extends Omit<IconButtonExProps, 'onClick'> {
    templateId: string;
    templateName: string;
    onAdd?: (id: string) => Promise<void>;
    icon?: JSX.Element;
}

const TemplateCopyPopup = ({ templateId, templateName, onAdd = async () => { }, icon = <ContentCopyIcon />, ...btnProps }: TemplateCopyProps) => {

    const [ErrorDisplay, setError] = useErrorDisplay();
    const [busy, setBusy] = useState<boolean>(false);
    const [open, setOpen] = useState<boolean>(false);
    const theme = useTheme();

    const [template, setTemplate] = useState<Template>(newTemplate);
    const disableSave = IsEmpty(template?.Name || template?.PositionId);

    const title = `Copy Template: '${templateName}'`;

    const handleSubmit = async () => {
        try {
            setBusy(true);

            let ct = {
                PositionId: template.PositionId,
                TemplateName: template.Name
            };

            let id = await new TemplateClient().Copy(templateId, ct);
            await onAdd(id);
            
            setOpen(false);

        } catch (error) {
            setError(error, TemplateErrors.Copy);
        }
        finally {
            setBusy(false);
        }
    };

    const handleInputChange = e => {
        const { name, value } = e.target
        setTemplate({ ...template, [name]: value })
    };

    const handleUserSelect = (positionId) => {
        setTemplate({ ...template, PositionId: positionId })
    }

    const handleOpen = async () => {
        setOpen(true);
    };


    return <>
        <ErrorDisplay />
        <IconButtonEx disabled={busy} hoverContent={title} onClick={handleOpen} {...btnProps}>{icon}</IconButtonEx>

        <ResponsiveDialog aria-labelledby="template-copy-title" open={open} tabletFullScreen="never" PaperProps={{ sx: { overflow: 'hidden', '& > div': { flex: '1 1 auto', minHeight: 0, height: 'auto !important', display: 'flex', flexDirection: 'column' } } }}>
            <BusySpinner busy={busy}>
                <ResponsiveDialogHeader id="template-copy-title" title={title} sx={{ margin: 0, padding: theme.spacing(2) }} />
                <DialogContent dividers sx={{ padding: theme.spacing(2), overflowY: 'auto' }}>
                    <MobileFormStack tabletColumns={2} desktopColumns={1}>
                        <TextField fullWidth autoFocus
                            name="Name"
                            label={I18N.Translate('Name')}
                            sx={{ marginBottom: theme.spacing(1) }}
                            error={IsEmpty(template?.Name)}
                            size='small'
                            variant="outlined"
                            value={template?.Name}
                            onChange={handleInputChange} />

                        <PositionAutocompleteControl positionId={template.PositionId} onUserSelect={handleUserSelect} />
                    </MobileFormStack>
                </DialogContent>
                <StickyDialogActions phoneLayout="stacked" sx={{ margin: 0, padding: theme.spacing(1) }}>
                    <Button onClick={() => setOpen(false)} variant='text' color='inherit'>Cancel</Button>
                    <Button disabled={disableSave} onClick={handleSubmit} variant='outlined' color='primary'>Submit</Button>
                </StickyDialogActions>
            </BusySpinner>
        </ResponsiveDialog>
    </>;
}

export default TemplateCopyPopup;
